import { isToday, isYesterday } from 'date-fns'
import { type ReactNode, useState } from 'react'
import { getDisplayName } from '~/entities/reviewer'
import type { Flag, FlagComment } from '~/shared/api/data-ingestion-api/client'
import { formatDateDefault, formatTimeDefault } from '~/shared/lib/datetime'
import {
  Button,
  LocalIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  Textarea,
} from '~/shared/ui'
import { getSortedComments } from '../lib/flag-comments'

interface LocalDraftProps {
  mode: 'local-draft'
  flag: null
  localDraft: string | null
  onSaveDraft: (text: string) => void
  onRemoveDraft?: () => void
}

interface PersistedProps {
  mode: 'persisted'
  flag: Flag
  canEdit: boolean
  onAddComment?: (text: string) => void | Promise<void>
  onResolve?: (text: string) => void | Promise<void>
  busy?: boolean
}

type FlagCommentsPopoverProps = {
  trigger: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
} & (LocalDraftProps | PersistedProps)

export function FlagCommentsPopover(props: FlagCommentsPopoverProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const open = props.open ?? internalOpen
  const setOpen = props.onOpenChange ?? setInternalOpen

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
      }}
    >
      <PopoverTrigger asChild>
        <span>{props.trigger}</span>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="flex w-[340px] flex-col gap-2 p-3"
      >
        {props.mode === 'local-draft' ? (
          <LocalDraftBody
            draft={props.localDraft}
            onSave={(text) => {
              props.onSaveDraft(text)
              setOpen(false)
            }}
            onRemove={
              props.onRemoveDraft
                ? () => {
                    props.onRemoveDraft?.()
                    setOpen(false)
                  }
                : undefined
            }
            onCancel={() => setOpen(false)}
          />
        ) : (
          <PersistedBody
            flag={props.flag}
            canEdit={props.canEdit}
            busy={props.busy}
            onAddComment={async (text) => {
              await props.onAddComment?.(text)
            }}
            onResolve={async (text) => {
              setOpen(false)
              await props.onResolve?.(text)
            }}
          />
        )}
      </PopoverContent>
    </Popover>
  )
}

function LocalDraftBody({
  draft,
  onSave,
  onRemove,
  onCancel,
}: {
  draft: string | null
  onSave: (text: string) => void
  onRemove?: () => void
  onCancel: () => void
}) {
  const [text, setText] = useState(draft ?? '')
  const isEdit = draft !== null
  const disabled = text.trim() === '' || (isEdit && text === draft)

  return (
    <>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Add a comment"
        rows={3}
      />
      <div className="flex items-center justify-between">
        {onRemove ? (
          <Button
            size="small"
            onClick={onRemove}
          >
            Remove Flag
          </Button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-4">
          <Button
            size="small"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            size="small"
            raised
            onClick={() => onSave(text)}
            disabled={disabled}
          >
            {isEdit ? 'Update' : 'Save'}
          </Button>
        </div>
      </div>
    </>
  )
}

function PersistedBody({
  flag,
  canEdit,
  busy,
  onAddComment,
  onResolve,
}: {
  flag: Flag
  canEdit: boolean
  busy?: boolean
  onAddComment: (text: string) => Promise<void>
  onResolve: (text: string) => Promise<void>
}) {
  const [text, setText] = useState('')
  const comments = getSortedComments(flag)
  const isResolved = flag.flag_status === 'resolved'
  const disabled = text.trim() === '' || busy === true

  async function handleSave() {
    await onAddComment(text)
    setText('')
  }

  async function handleResolve() {
    await onResolve(text)
    setText('')
  }

  return (
    <>
      <Text appearance="OVERLINE">Comments</Text>
      <div className="flex max-h-[240px] flex-col gap-2 overflow-y-auto">
        {comments.length > 0 ? (
          comments.map((comment) => (
            <CommentRow
              key={comment.key}
              comment={comment}
            />
          ))
        ) : (
          <Text
            appearance="CAPTION"
            className="text-text-secondary"
          >
            No comments yet.
          </Text>
        )}
      </div>
      {canEdit && !isResolved && (
        <>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a comment or a reason for resolving this flag"
            rows={3}
          />
          <div className="flex items-center justify-between">
            <Button
              size="small"
              materialIcon="check_small"
              onClick={handleResolve}
              disabled={disabled}
            >
              Resolve Flag
            </Button>
            <Button
              size="small"
              raised
              onClick={handleSave}
              disabled={disabled}
            >
              Save Comment
            </Button>
          </div>
        </>
      )}
    </>
  )
}

function CommentRow({ comment }: { comment: FlagComment }) {
  const date = new Date(comment.created_at)
  return (
    <div className="flex flex-col py-1">
      <div className="flex min-w-0 items-start gap-1">
        <div className="text-text-secondary flex shrink-0 items-center gap-2 whitespace-nowrap">
          <Text
            className="uppercase"
            appearance="CAPTION"
          >
            {formatTimeDefault(date.getTime())}
          </Text>
          <span className="bg-text-secondary h-1 w-1 rounded-full" />
          <Text
            className="uppercase"
            appearance="CAPTION"
            title={isToday(date) || isYesterday(date) ? formatDateDefault(date) : undefined}
          >
            {isToday(date) ? 'Today' : isYesterday(date) ? 'Yesterday' : formatDateDefault(date)}
          </Text>
          <LocalIcon
            name="personOutlined"
            size="16"
          />
        </div>
        <Text
          className="overflow-hidden"
          appearance="CAPTION"
        >
          {getDisplayName(comment.user)}
        </Text>
      </div>
      {comment.notes && (
        <Text
          className="mt-1"
          appearance="CAPTION"
        >
          {comment.notes}
        </Text>
      )}
    </div>
  )
}
